import React, { useContext, useState } from 'react';
import { HistoryOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Modal, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { isNotNull } from '@totem/utilities/common';

const { Text } = Typography;

const styles = {
  form: {
    width: '100%',
    marginTop: 16,
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
};

type Props = {
  style?: React.CSSProperties;
};

const OptimizeDeferralButton = ({ style }: Props) => {
  const { ticket, onOptimizeDeferral } = useContext(TicketContext);
  const [showDialog, setShowDialog] = useState(false);
  const [deferUntil, setDeferUntil] = useState<Dayjs | null>(null);
  const [notes, setNotes] = useState('');

  const canDefer = true;

  const handleClose = () => {
    setShowDialog(false);

    setDeferUntil(null);

    setNotes('');
  };

  const handleSubmit = () => {
    if (isNotNull(ticket) && isNotNull(ticket.ticket) && deferUntil !== null) {
      onOptimizeDeferral({
        deferUntil: deferUntil.startOf('day').toISOString(),
        notes: notes.trim() || undefined,
      });

      handleClose();
    }
  };

  // Only allow dates starting from tomorrow
  const disablePastDates = (date: Dayjs) =>
    date.isBefore(dayjs().add(1, 'day').startOf('day'));

  return (
    <>
      <Button
        style={style}
        disabled={!canDefer || showDialog}
        onClick={() => setShowDialog(true)}
        icon={<HistoryOutlined />}
      >
        Defer
      </Button>
      {canDefer && showDialog && (
        <Modal
          width={520}
          open={showDialog}
          title="Defer Opportunity"
          onCancel={handleClose}
          onOk={handleSubmit}
          footer={[
            <Button key="cancel" onClick={handleClose}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              disabled={deferUntil === null}
              onClick={handleSubmit}
            >
              Defer
            </Button>,
          ]}
        >
          <Text type="secondary" style={{ fontSize: 13 }}>
            Deferring moves this opportunity out of the active queue until the
            selected date, when it will return to previous state.
          </Text>
          <Form layout="vertical" style={styles.form}>
            <Form.Item label="Defer Until" required style={styles.formItem}>
              <DatePicker
                value={deferUntil}
                onChange={setDeferUntil}
                disabledDate={disablePastDates}
                placeholder="Select a future date"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Reason (optional)" style={styles.formItem}>
              <Input.TextArea
                rows={3}
                value={notes}
                onChange={(evt) => setNotes(evt.target.value)}
                placeholder="e.g. Waiting for budget approval in Q3, revisit in September"
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default OptimizeDeferralButton;
